import { PlusIcon } from '@phosphor-icons/react';
import { FormikProvider } from 'formik';
import { useFormik } from 'components/form/formik';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { TextAreaField } from 'components/textarea/TextAreaField';
import { SimpleComboboxField } from 'components/combobox/SimpleComboboxField';
import { Country, DocumentType, QuotationType } from 'src/types/enums';
import { invariant } from '@utils/invariant';
import { createQuotation } from '../../models';
import { getDisplayError } from '@utils/get-display-error';
import { parseNumberInput } from '@utils/number';
import { formatInputDate } from '@utils/date';
import { useMinimalGeneralSettings } from 'contexts/minimal-settings-context';

import { useAIDraftCtx } from './context';
import { QUOTATION_TYPE_OPTIONS } from '../../constants';

const draftOfferteSchema = Yup.object().shape({
  reference: Yup.string().required('Vereist'),
  quotationDate: Yup.string().required('Vereist'),
  expiresAt: Yup.string().required('Vereist'),
  notes: Yup.string(),
});

export const DraftOfferteStep = () => {
  const navigate = useNavigate();
  const { state } = useAIDraftCtx();
  const generalSettings = useMinimalGeneralSettings();

  const formikbag = useFormik({
    initialValues: {
      type: QUOTATION_TYPE_OPTIONS[0],
      reference: '',
      quotationDate: formatInputDate(new Date()),
      expiresAt: formatInputDate(new Date()),
      notes: '',
    },
    validationSchema: draftOfferteSchema,
    onSubmit: async (values) => {
      try {
        const customerId = state.customer?.id;
        invariant(customerId, 'Selecteer eerst een klant in stap 2');

        const result = await createQuotation({
          type: values.type.key as QuotationType,
          customerId,
          reference: values.reference,
          notes: values.notes,
          quotationDate: values.quotationDate,
          expiresAt: values.expiresAt,
          lines: state.lanes.map((l) => ({
            departurePostalCode: l.departurePostalCode,
            departureCountry: l.departureCountry.key as Country,
            departureCity: l.departureCity,
            arrivalPostalCode: l.arrivalPostalCode,
            arrivalCountry: l.arrivalCountry.key as Country,
            arrivalCity: l.arrivalCity,
            distance: l.distance ? parseNumberInput(l.distance, 2) : 0,
            price: l.price ? parseNumberInput(l.price, 2) : 0,
            trailerTypes: l.trailerTypes,
          })),
        });

        trackEvent('quotation_created_from_ai_draft', {
          quotation_id: result.id,
          customer_id: customerId,
          quotation_type: values.type.key,
          line_count: state.lanes.length,
        });
        navigate(`/internal/finance/quotations/${result.id}`, { replace: true });
        toast.success('Offerte aangemaakt');
      } catch (err: any) {
        captureException(err);
        toast.error('Kon offerte niet aanmaken: ' + getDisplayError(err));
      }
    },
  });

  const { values, isSubmitting, handleSubmit, setFieldValue } = formikbag;

  // Auto-set expiresAt based on quotation type
  const quotationType = values.type;
  useEffect(() => {
    const getEndDate = () => {
      if (!values.quotationDate) {
        return new Date();
      }
      const date = dayjs(values.quotationDate);
      if (quotationType.key === QuotationType.Spot) {
        return date.add(14, 'day');
      } else {
        return date.endOf('year');
      }
    };
    setFieldValue('expiresAt', formatInputDate(getEndDate()));
  }, [quotationType, values.quotationDate]);

  // Auto-set notes from document settings when customer is set
  const customer = state.customer;
  useEffect(() => {
    if (!customer) return;

    const note = generalSettings.settings.documentNotes.find(
      (v) => v.language === customer.language && v.documentType === DocumentType.Quotation,
    );
    if (note) {
      setFieldValue('notes', note.content);
    }
  }, [customer]);

  return (
    <div className="px-4">
      <FormikProvider value={formikbag}>
        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <Card title="Offerte gegevens">
              <SimpleComboboxField items={QUOTATION_TYPE_OPTIONS} name="type" labelText="Offerte type" />

              <div className="mb-4 p-3 bg-neutral-subtle rounded">
                <span className="font-medium">Klant: </span>
                {state.customer ? (
                  state.customer.name
                ) : (
                  <span className="text-negative-text">Geen klant geselecteerd</span>
                )}
              </div>

              <InputField type="text" labelText="Referentie klant" name="reference" isDisabled={isSubmitting} />

              <div className="grid grid-cols-2 gap-4">
                <InputField labelText="Offertedatum" type="date" name="quotationDate" />
                <InputField labelText="Vervalt op" type="date" name="expiresAt" />
              </div>

              <TextAreaField labelText="Notities" name="notes" isDisabled={isSubmitting} spellCheck={true} />
            </Card>
          </div>

          <div className="mb-8">
            <Card title={`Lijnen (${state.lanes.length})`}>
              <div className="grid gap-3">
                {state.lanes.map((lane, idx) => (
                  <div key={`summary-lane-${idx}`} className="flex items-center gap-4 p-3 bg-surface-sunken rounded">
                    <span className="font-medium text-fg-secondary w-8">{idx + 1}.</span>
                    <div className="flex-1">
                      <span>
                        {lane.departurePostalCode} {lane.departureCity} ({lane.departureCountry.key})
                      </span>
                      <span className="mx-2 text-fg-muted">&rarr;</span>
                      <span>
                        {lane.arrivalPostalCode} {lane.arrivalCity} ({lane.arrivalCountry.key})
                      </span>
                    </div>
                    <div className="text-right">
                      {lane.distance && <span className="text-fg-muted mr-4">{lane.distance} km</span>}
                      {lane.price && <span className="font-medium">&euro; {lane.price}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="mt-8">
            <Button
              type="submit"
              color="primary"
              isDisabled={isSubmitting || !state.customer}
              isLoading={isSubmitting}
              iconLeft={<PlusIcon className="button-icon" />}
            >
              Maak offerte aan
            </Button>
          </div>
        </form>
      </FormikProvider>
    </div>
  );
};
